import React, { useCallback, useEffect, useState } from "react";
import { toDecimal } from "../../helpers/PriceShopEngine";
import { motion } from "framer-motion";
import {
  BadgeEuro,
  ListChecks,
  Server,
  ShoppingCart,
  SquareSigma,
  User2Icon,
  StickyNote,
} from "lucide-react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import OrderNotesSection from "./OrderNotesSection";
import NotesSection from "../Shared/NotesSection";

type ComponentRow = Order["items"][number]["selected_components"][number];

// cost_price/rev3_price are per-unit; component.price is already the line total
const costTotal = (c: ComponentRow) => (c.cost_price ?? 0) * (c.quantity ?? 0);
const rev3Total = (c: ComponentRow) => (c.rev3_price ?? 0) * (c.quantity ?? 0);

interface ManageOrderProps {
  order: Order;
  onAddNote?: (content: string, visibleToReseller: boolean) => void;
}

const ManageOrder: React.FC<ManageOrderProps> = ({
  order,
  onAddNote,
}: ManageOrderProps) => {
  const [tab, setTab] = React.useState("order");
  const [offerReviewHtml, setOfferReviewHtml] = useState<string>("");

  const generateOfferPreview = useCallback(async () => {
    try {
      const response = await api.get(
        API_URLS.OFFERS + "/" + order.offer_id + "/_actions/review",
      );
      console.log("Preview gerado com sucesso!", response.data);
      return response.data as string;
    } catch (error) {
      console.warn("Erro ao gerar preview:", error);
      return "";
    }
  }, [order.offer_id]);

  useEffect(() => {
    generateOfferPreview().then((html) => setOfferReviewHtml(html));
  }, [generateOfferPreview]);

  function getPriceToShow() {
    if (
      order.offer_client_type === "END_CUSTOMER" &&
      order.order_type === "INTERNAL"
    ) {
      return order.items[0].customer_price;
    }
    return order.total_amount;
  }

  function renderClientInfo(client: Client) {
    return (
      <div className="column">
        <h3 className="title is-4 mb-2">{client?.name}</h3>
        <p className="mb-1">
          {client?.id && (
            <span className="w-5/12 is-inline-block">
              <strong>Nº Cliente:</strong>{" "}
              {client?.id ? client?.id : <small>n/a</small>}
            </span>
          )}
          {client?.vat && (
            <span className="w-5/12 is-inline-block">
              <strong>NIF:</strong>{" "}
              {client?.vat ? client?.vat : <small>n/a</small>}
            </span>
          )}
        </p>
        <p className="mb-1">
          {client?.phone && (
            <span className="w-5/12 is-inline-block">
              <strong>Telefone:</strong>{" "}
              {client?.phone ? client?.phone : <small>n/a</small>}{" "}
            </span>
          )}
          {client?.email && (
            <span className="w-5/12 is-inline-block">
              <strong>Email:</strong>{" "}
              {client?.email ? client?.email : <small>n/a</small>}{" "}
            </span>
          )}
        </p>
        {(client?.address || client?.zip_code || client?.city) && (
          <p className="mb-1">
            <strong>Morada:</strong> {client?.address} <br /> {client?.zip_code}{" "}
            {client?.city}
          </p>
        )}
      </div>
    );
  }

  function renderSummaryInfo() {
    return (
      <>
        <div className="column has-text-left">
          <span>
            <strong>Origem da Encomenda:</strong>{" "}
            {order.order_type === "INTERNAL" ? "Comercial" : "Revendedor"}
          </span>
          <br />
          {order.order_type === "INTERNAL" && (
            <>
              <span>
                <strong>Tipo de Cliente:</strong>{" "}
                {order.offer_client_type === "RESELLER"
                  ? "Revendedor"
                  : order.offer_client_type === "END_CUSTOMER"
                    ? "Cliente Final"
                    : "Outro"}
              </span>
              <br />
            </>
          )}
          <span>
            <strong>Proposta:</strong> {order.offer_external_id}
          </span>
          <br />
          <span>
            <strong>Comercial:</strong> {order.commercial_portfolio} -{" "}
            {order.commercial_agents
              .map((c) => c.name || c.username)
              .join(", ")}
          </span>
        </div>
        <div className="column is-narrow has-text-right">
          <span className="tag is-info is-medium mb-3">
            {getPriceToShow().toLocaleString("pt-PT", {
              style: "currency",
              currency: "EUR",
            })}
          </span>
          <br />
          <small>
            Criada a: {new Date(order.created_at).toLocaleString("pt-PT")}
          </small>
        </div>
      </>
    );
  }

  function renderErpStatus() {
    if (
      order.status === "PENDING" ||
      order.status === "REJECTED" ||
      order.status === "CANCELLED"
    ) {
      return null;
    }
    const status = order.erp_submission_status;
    const config: Partial<
      Record<
        NonNullable<Order["erp_submission_status"]>,
        { label: string; cls: string; hint?: string }
      >
    > = {
      SENT: { label: "Enviada para o ERP", cls: "has-text-success" },
      PENDING: { label: "A enviar para o ERP", cls: "has-text-warning" },
      SENDING: { label: "A enviar para o ERP", cls: "has-text-warning" },
      UNKNOWN: {
        label: "Estado no ERP por confirmar",
        cls: "has-text-warning",
        hint: "O ERP pode já ter criado a encomenda. Confirme no ERP antes de reenviar.",
      },
      FAILED: { label: "Falha ao enviar para o ERP", cls: "has-text-danger" },
    };
    const current = status
      ? (config[status] ?? {
          label: "Estado no ERP desconhecido",
          cls: "has-text-grey",
        })
      : order.erp_submission_enabled === false
        ? { label: "Integração ERP desativada", cls: "has-text-grey" }
        : { label: "Sem registo de envio no ERP", cls: "has-text-grey" };
    return (
      <div className="box has-background-light p-4 mb-4">
        <div className="columns is-align-items-start">
          <div className="column is-narrow">
            <Server className={`icon is-medium ${current.cls}`} />
          </div>
          <div className="column">
            <p>
              <strong>ERP: </strong>
              <span className={current.cls}>{current.label}</span>
              {order.erp_order_number && (
                <>
                  {" "}
                  &mdash; Nº ERP{" "}
                  <span className="font-mono">{order.erp_order_number}</span>
                </>
              )}
            </p>
            {current.hint && (
              <p className="is-size-7 has-text-grey mt-1">{current.hint}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto">
      <div className="box">
        <h2 className="title is-2">
          Encomenda #{order.id} - {order.status}
        </h2>

        <div className="tabs is-boxed">
          <ul>
            <li className={tab === "order" ? "is-active" : ""}>
              <a onClick={() => setTab("order")}>
                <span className="icon is-small">
                  <ShoppingCart />
                </span>
                <span>Encomenda</span>
              </a>
            </li>
            <li className={tab === "offer-review" ? "is-active" : ""}>
              <a onClick={() => setTab("offer-review")}>
                <span className="icon is-small">
                  <BadgeEuro />
                </span>
                <span>
                  Proposta{" "}
                  {order.order_type == "DEFAULT"
                    ? "do revendedor ao cliente"
                    : order.offer_client_type == "END_CUSTOMER"
                      ? "ao cliente final"
                      : "ao revendedor"}
                </span>
              </a>
            </li>
            <li className={tab === "notes" ? "is-active" : ""}>
              <a onClick={() => setTab("notes")}>
                <span className="icon is-small">
                  <StickyNote />
                </span>
                <span>Notas</span>
              </a>
            </li>
          </ul>
        </div>

        <div
          className={`content ${tab === "order" ? "" : "is-hidden"}  min-h-[75vh] flex flex-col`}
        >
          <div className="resume-card box has-background-light p-4 mb-4">
            <div className="columns is-align-items-start">
              <div className="column is-narrow">
                <SquareSigma className="icon is-medium has-text-primary" />
              </div>
              {renderSummaryInfo()}
            </div>
          </div>

          {renderErpStatus()}

          <div className="client-card box has-background-light p-4 mt-4 mb-4">
            <div className="columns is-align-items-start">
              <div className="column is-narrow">
                <User2Icon className="icon is-medium has-text-primary" />
              </div>
              {order.order_type === "INTERNAL"
                ? renderClientInfo(order.offer_client)
                : renderClientInfo(order.client)}
            </div>
          </div>

          {order.items.map((item, idx) => (
            <div key={item.compound_id} className="item-card">
              <div key={item.compound_id} className="item-card">
                <div className="item-info-card box has-background-light p-4 mb-4">
                  <div className="columns is-align-items-start">
                    <div className="column is-narrow">
                      <ListChecks className="icon is-medium has-text-primary" />
                    </div>

                    <div className="column">
                      <div className="grid grid-cols-5 gap-4 items-start">
                        <div>
                          <strong>Referência:</strong>
                          <br />
                          <span className="font-mono">{item.compound_id}</span>
                        </div>
                        <div>
                          <strong>Custo Niposom:</strong>
                          <br />
                          <span className="font-mono">
                            {item.selected_components
                              .reduce((sum, comp) => sum + costTotal(comp), 0)
                              .toLocaleString("pt-PT", {
                                style: "currency",
                                currency: "EUR",
                              })}
                            <br />
                            <small>(valor sem IVA)</small>
                          </span>
                        </div>
                        <div>
                          <strong>Preço de REV3:</strong>
                          <br />
                          <span className="font-mono">
                            {item.selected_components
                              .reduce((sum, comp) => sum + rev3Total(comp), 0)
                              .toLocaleString("pt-PT", {
                                style: "currency",
                                currency: "EUR",
                              })}
                            <br />
                            <small>(valor sem IVA)</small>
                          </span>
                        </div>
                        <div>
                          <strong>Desconto na margem:</strong>
                          <br />
                          <span className="font-mono">
                            {item.discount_value} %
                          </span>
                        </div>
                        <div>
                          <strong>Quantidade:</strong>
                          <br />
                          <span className="font-mono">{item.quantity}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-start mt-4">
                        <div></div>
                        <div>
                          <strong>PVP da venda:</strong>
                          <br />
                          <span className="font-mono">
                            {(item.customer_price ?? 0).toLocaleString(
                              "pt-PT",
                              {
                                style: "currency",
                                currency: "EUR",
                              },
                            )}
                            <br />
                            <small>(valor com IVA)</small>
                          </span>
                        </div>
                        <div>
                          <strong>Preço Loja (Base):</strong>
                          <br />
                          <span className="font-mono">
                            {(item.base_price ?? 0).toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                            <br />
                            <small>(valor sem IVA)</small>
                          </span>
                        </div>
                        <div>
                          <strong>Preço Loja (Opcionais):</strong>
                          <br />
                          <span className="font-mono">
                            {(item.sell_price ?? 0).toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                            <br />
                            <small>(valor sem IVA)</small>
                          </span>
                        </div>
                        <div>
                          <strong>Margem do Revendedor:</strong>
                          <br />
                          <span className="font-mono">
                            {toDecimal(item.margin_reseller)} %
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="components-info-card p-4 mb-4">
                <div className="table-container overflow-x-auto">
                  <table className="table is-bordered is-striped is-narrow is-hoverable is-fullwidth font-mono">
                    <thead>
                      <tr className="has-text-centered has-text-weight-bold bg-slate-400">
                        <td>Componente</td>
                        <td>Referência</td>
                        <td>Nome</td>
                        <td>Qde</td>
                        <td>Valor unit.</td>
                        <td>Custo (total)</td>
                        <td>REV3 (total)</td>
                        <td>Valor</td>
                      </tr>
                    </thead>
                    <tbody>
                      {item.selected_components.map((component) => (
                        <tr key={component.component_id}>
                          <td>{component.product_type}</td>
                          <td>{component.product_reference}</td>
                          <td>{component.product_name}</td>
                          <td align="center">{component.quantity}</td>
                          <td align="right">
                            {(
                              component.price / component.quantity
                            ).toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                          </td>
                          <td align="right">
                            {costTotal(component).toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                          </td>
                          <td align="right">
                            {rev3Total(component).toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                          </td>
                          <td align="right">
                            {(component.price ?? 0).toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="has-text-centered has-text-weight-bold bg-slate-200">
                        <td colSpan={5}>Total</td>
                        <td align="right">
                          {item.selected_components
                            .reduce((sum, comp) => sum + costTotal(comp), 0)
                            .toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                        </td>
                        <td align="right">
                          {item.selected_components
                            .reduce((sum, comp) => sum + rev3Total(comp), 0)
                            .toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                        </td>
                        <td align="right">
                          {/* price is already the line total (per-unit x quantity); do not multiply again */}
                          {item.selected_components
                            .reduce((sum, comp) => sum + (comp.price ?? 0), 0)
                            .toLocaleString("pt-PT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`content ${tab === "offer-review" ? "" : "is-hidden"} min-h-[75vh] flex flex-col`}
        >
          <iframe
            srcDoc={offerReviewHtml}
            className="w-full h-full"
            style={{ flex: 1, width: "100%", height: "100%" }}
          />
        </div>

        <div
          className={`content ${tab === "notes" ? "" : "is-hidden"} min-h-[75vh]`}
        >
          <NotesSection
            entityId={Number(order.id) || 0}
            entityType="order"
            showVisibilityOption={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ManageOrder;
